import AddFavModal from "@shared/AddFavModal";
import DynamicDropdown from "@components/base/DynamicDropDown/DynamicDropDown";
import { captureException } from "@sentry/react";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import ContactModal from "@shared/country-components/contactModal";
import ReactTableTrademo from "@shared/trademo-datatable/react-table-trademo";
import { getApiUrl } from "@utils/baseUrl";
import { getIntelUrl } from "@utils/intelUrl";
import commaFormat from "@utils/commaFormat";
import { useDebounce } from "@utils/utils";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { getRemainingValueAddCredits } from "@core/utility/utility";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { getUserActivePlan } from "@helpers/profileHelper";
import axios from "axios";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getCreditsByPlanHandler } from "../../redux/User/userDetails.action";
import { getSidebarCountData } from "../../redux/Sidebar/sidebarCount.action";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import IntelLayout from "../../components/layout/layout";
import ColumnConfigurationModal from "./components/ColumnConfigurationModal";
import EnrichmentInputsModal from "./components/EnrichmentInputsModal";
import WorkspaceListCriteriaModal, { hasWorkspaceListCriteria } from "./components/WorkspaceListCriteriaModal";
import type { WorkspaceItem, WorkspaceColumn, TradingPartnersCacheEntry, WorkspaceRow, SortConfig, EnrichmentOverrideEntry, UserDetailsState, SlugOptions, TrademoTableState, ContactEnrichmentParams, PricingAgentItem, SanctionMatch, SanctionsScreeningResult } from "./utils/types";
import EnrichedContactsPopover from "./components/EnrichedContactsPopover";
import PricingAgentPane from "./components/PricingAgentPane";
import SanctionsScreeningPane from "./components/SanctionsScreeningPane";
import WorkspaceDownloadControl from "./components/WorkspaceDownloadControl";
import WorkspaceTabs from "./components/WorkspaceTabs";
import ConfirmActionModal from "./components/ConfirmActionModal";
import WorkspaceEmptyState from "./components/WorkspaceEmptyState";
import WorkspaceFiltersPanel, { type WorkspaceFiltersValue, type FilterKey, countActiveFilters, isFilterActive } from "./components/WorkspaceFiltersPanel";
import { CustomStylesWorkspaceTable, workspaceApiEndpoints, getErrorMessage, WorkspaceColumnKey, enrichmentColumnTooltips, getAvatarBg, normalizeTradingPartnersPayload, extractColumnsPayload, mapWorkspaceRowToEnrichmentCompany, formatAiSummaryHtml, getWorkspaceRowKey, INFO_ICON_COLUMNS, RIGHT_ALIGN_LABELS, DATE_SORT_LABELS, COLUMN_WIDTHS, getTradingPartnersCellKey } from "./utils/constants";
import { Tooltip } from "react-tooltip5";
import { CLOUDINARY_INTEL_BASE_URL } from "../../utils/enum";
import ReactCountryFlag from "react-country-flag";
import Loader from "@components/base/loader/Loader";

const buildFilterParams = (filters: WorkspaceFiltersValue): Record<string, string> =>
  Object.fromEntries(
    Object.entries(filters)
      .filter(([, v]) => isFilterActive(v as WorkspaceFiltersValue[FilterKey]))
      .map(([k, v]) => [k, JSON.stringify(v)])
  );

const Workspace = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [columnEnrichMenuKey, setColumnEnrichMenuKey] = useState<string | null>(null);
  const [showMoreActionsMenu, setShowMoreActionsMenu] = useState(false);
  const [selectedRows, setSelectedRows] = useState<WorkspaceRow[]>([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [rowPerPage, setRowPerPage] = useState(25);
  const [pageNumber, setPageNumber] = useState(1);
  const [workspaceList, setWorkspaceList] = useState<WorkspaceItem[]>([]);
  const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceItem>({});
  const [workspaceCompanies, setWorkspaceCompanies] = useState<WorkspaceRow[]>([]);
  const [workspaceCompaniesCount, setWorkspaceCompaniesCount] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: null, order: "desc" });
  const sortConfigRef = useRef<SortConfig>({ field: null, order: "desc" });
  const [workspaceColumns, setWorkspaceColumns] = useState<WorkspaceColumn[]>([]);
  const [showColumnConfigModal, setShowColumnConfigModal] = useState(false);
  const [columnConfigMode, setColumnConfigMode] = useState<"all" | "enrichment">("all");
  const [showContactModal, setShowContactModal] = useState(false);
  const [showContactsDrop, setShowContactsDrop] = useState(false);
  const [showFavModal, setShowFavModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [contactEnrichingRowKeys, setContactEnrichingRowKeys] = useState<string[]>([]);
  const [companyInfoEnrichingRowKeys, setCompanyInfoEnrichingRowKeys] = useState<string[]>([]);
  const [pricingLoadingRowKeys, setPricingLoadingRowKeys] = useState<string[]>([]);
  const [aiSummaryLoadingRowKeys, setAiSummaryLoadingRowKeys] = useState<string[]>([]);
  const [showEnrichmentInputsModal, setShowEnrichmentInputsModal] = useState(false);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<WorkspaceItem | null>(null);
  const [showRemoveCompaniesModal, setShowRemoveCompaniesModal] = useState(false);
  const [lastUsedHsCode, setLastUsedHsCode] = useState("");
  const [pendingEnrichmentRows, setPendingEnrichmentRows] = useState<WorkspaceRow[] | null>(null);
  const [showAiSummaryModal, setShowAiSummaryModal] = useState(false);
  const [activeAiSummaryHtml, setActiveAiSummaryHtml] = useState("");
  const [aiSummaryCompanyName, setAiSummaryCompanyName] = useState("");
  const [showPricingPane, setShowPricingPane] = useState(false);
  const [activePricing, setActivePricing] = useState<PricingAgentItem[]>([]);
  const [pricingCompanyName, setPricingCompanyName] = useState("");
  const [sanctionsLoadingRowKeys, setSanctionsLoadingRowKeys] = useState<string[]>([]);
  const [traderManufacturerLoadingRowKeys, setTraderManufacturerLoadingRowKeys] = useState<string[]>([]);
  const [showSanctionsPane, setShowSanctionsPane] = useState(false);
  const [activeSanctions, setActiveSanctions] = useState<SanctionMatch[]>([]);
  const [sanctionsCompanyName, setSanctionsCompanyName] = useState("");
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(true);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(true);
  const [isColumnsLoading, setIsColumnsLoading] = useState(true);
  const [activeTradingPartnersCell, setActiveTradingPartnersCell] = useState<string | null>(null);
  const [activeTradingCountriesCell, setActiveTradingCountriesCell] = useState<string | null>(null);
  const [activeEnrichedContactCell, setActiveEnrichedContactCell] = useState<string | number | null>(null);
  const [activeCompetitorsCell, setActiveCompetitorsCell] = useState<string | null>(null);
  const [tradingPartnersMap, setTradingPartnersMap] = useState<Record<string, TradingPartnersCacheEntry>>({});
  const [popoverAlignRight, setPopoverAlignRight] = useState({ tradingPartners: false, tradingCountries: false, competitors: false });
  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 300);
  const debouncedSearchTermRef = useRef<string>(debouncedSearchTerm);
  const activeWorkspaceIdRef = useRef<string | number | undefined>(activeWorkspace?.id);
  const previousDebouncedSearchRef = useRef<string>(debouncedSearchTerm);
  const companiesRequestIdRef = useRef<number>(0);
  const columnsRequestIdRef = useRef<number>(0);
  const moreActionsRef = useRef<HTMLDivElement>(null);
  const tradingPartnersRef = useRef<HTMLDivElement>(null);
  const tradingCountriesRef = useRef<HTMLDivElement>(null);
  const enrichedContactsRef = useRef<HTMLDivElement>(null);
  const competitorsRef = useRef<HTMLDivElement>(null);
  const sidebarSetterRef = useRef<((open: boolean) => void) | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [enrichmentOverrides, setEnrichmentOverrides] = useState<Record<string, EnrichmentOverrideEntry>>({});
  const dispatch = useDispatch();
  const userDetails = useSelector((state: { userDetails: UserDetailsState }) => state.userDetails);
  const userFirstPlan = userDetails?.creditDetails?.data?.[0] ?? null;
  const currentUserPlan = userFirstPlan?.user_active_plan;
  const remainingValueAddCount = getRemainingValueAddCredits({ value_add_credits: userFirstPlan?.value_add_credits ?? userDetails?.creditDetails?.credit_details?.value_add_credits });
  const userName = userDetails?.userData?.fullName || "";
  const isInternalUser = userDetails?.userData?.email?.toLowerCase()?.endsWith("@trademo.com") ?? false;
  const [sourcesModalCompanyId, setSourcesModalCompanyId] = useState<string | null>(null);
  const [sourcesData, setSourcesData] = useState<unknown>(null);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [workspaceFilters, setWorkspaceFilters] = useState<WorkspaceFiltersValue>({});
  const workspaceFiltersRef = useRef<WorkspaceFiltersValue>({});
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    debouncedSearchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  useEffect(() => {
    activeWorkspaceIdRef.current = activeWorkspace?.id;
  }, [activeWorkspace?.id]);

  useEffect(() => {
    if (!clearSelection) return undefined;
    const timer = setTimeout(() => setClearSelection(false), 0);
    return () => clearTimeout(timer);
  }, [clearSelection]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (moreActionsRef.current && !moreActionsRef.current.contains(target)) {
        setShowMoreActionsMenu(false);
      }
      if (columnEnrichMenuKey) {
        const insideColumnEnrichMenu = (target as Element).closest?.(`[data-workspace-column-enrich-root="${columnEnrichMenuKey}"]`);
        if (!insideColumnEnrichMenu) {
          setColumnEnrichMenuKey(null);
        }
      }
      if (tradingPartnersRef.current && !tradingPartnersRef.current.contains(target)) {
        setActiveTradingPartnersCell(null);
      }
      if (tradingCountriesRef.current && !tradingCountriesRef.current.contains(target)) {
        setActiveTradingCountriesCell(null);
      }
      if (enrichedContactsRef.current && !enrichedContactsRef.current.contains(target)) {
        setActiveEnrichedContactCell(null);
      }
      if (competitorsRef.current && !competitorsRef.current.contains(target)) {
        setActiveCompetitorsCell(null);
      }
      if (filtersRef.current && !filtersRef.current.contains(target)) {
        setShowFiltersPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [columnEnrichMenuKey]);

  useEffect(() => {
    let isMounted = true;

    const fetchWorkspaceList = async () => {
      setIsWorkspaceLoading(true);
      try {
        const { data } = await axios.get(workspaceApiEndpoints.getIntelWorkspaces);
        const normalizedList = data?.workspaces || [];
        if (!isMounted) return;
        setWorkspaceList(normalizedList);
        if (normalizedList.length > 0) {
          const stateId = (location.state as { workspaceId?: string | number } | null)?.workspaceId;
          const paramId = stateId !== undefined && stateId !== null ? String(stateId) : new URLSearchParams(window.location.search).get("workspace_id");
          const matched = paramId ? normalizedList.find((w: WorkspaceItem) => String(w.id) === paramId) : null;
          setActiveWorkspace(matched || normalizedList[0]);
        }
      } catch (error) {
        captureException(error);
        if (!isMounted) return;
        setWorkspaceList([]);
      } finally {
        if (isMounted) {
          setIsWorkspaceLoading(false);
        }
      }
    };

    fetchWorkspaceList();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWorkspaceCompanies = useCallback(async (workspaceId: string | number, search = "", silent = false, from = 0, sortField: string | null = null, sortOrder: "asc" | "desc" = "desc", filters?: WorkspaceFiltersValue): Promise<void> => {
    const requestId = companiesRequestIdRef.current + 1;
    companiesRequestIdRef.current = requestId;
    if (!silent) setIsCompaniesLoading(true);
    try {
      const activeFilters = filters ?? workspaceFiltersRef.current;
      const { data } = await axios.get(workspaceApiEndpoints.getIntelWorkspaceCompanies(workspaceId), {
        params: { search, from, size: 150, ...(sortField ? { sort_field: sortField, sort_order: sortOrder } : {}), ...buildFilterParams(activeFilters) },
      });
      const rawCompanies = data?.companies || [];
      if (requestId !== companiesRequestIdRef.current) return;
      if (from > 0) {
        setWorkspaceCompanies((prev) => [...prev, ...rawCompanies]);
      } else {
        setWorkspaceCompanies(rawCompanies);
      }
      setWorkspaceCompaniesCount(data.total_count || 0);
    } catch (error) {
      if (requestId !== companiesRequestIdRef.current) return;
      captureException(error);
      if (from === 0) {
        setWorkspaceCompanies([]);
        setWorkspaceCompaniesCount(0);
      }
    } finally {
      if (requestId === companiesRequestIdRef.current && !silent) {
        setIsCompaniesLoading(false);
      }
    }
  }, []);

  const fetchWorkspaceCompaniesLookup = useCallback(async (workspaceId: string | number, companyIds: string[]): Promise<void> => {
    if (!companyIds.length) return;
    try {
      const { data } = await axios.post(workspaceApiEndpoints.lookupIntelWorkspaceCompanies, {
        company_ids: companyIds,
        workspace_id: workspaceId,
      });
      if (data?.status === "success" && data?.data) {
        setWorkspaceCompanies((prev) =>
          prev.map((company) => {
            const updated = data.data[String(company.company_id)];
            return updated ? { ...company, ...updated } : company;
          })
        );
      }
    } catch (error) {
      captureException(error);
    }
  }, []);

  const fetchWorkspaceColumns = useCallback(async (workspaceId: string | number): Promise<void> => {
    const requestId = columnsRequestIdRef.current + 1;
    columnsRequestIdRef.current = requestId;
    setIsColumnsLoading(true);
    try {
      const { data } = await axios.get(workspaceApiEndpoints.getIntelWorkspaceColumns(workspaceId));
      const columns = extractColumnsPayload(data);
      if (requestId !== columnsRequestIdRef.current) return;
      setWorkspaceColumns(columns);
    } catch (error) {
      if (requestId !== columnsRequestIdRef.current) return;
      captureException(error);
      setWorkspaceColumns([]);
    } finally {
      if (requestId === columnsRequestIdRef.current) {
        setIsColumnsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const activeWorkspaceId = activeWorkspace?.id;
    if (!activeWorkspaceId) {
      setWorkspaceColumns([]);
      setWorkspaceCompanies([]);
      setWorkspaceCompaniesCount(0);
      return;
    }

    const loadWorkspaceGridData = async () => {
      setPageNumber(1);
      setSearchTerm("");
      debouncedSearchTermRef.current = "";
      previousDebouncedSearchRef.current = "";
      setSortConfig({ field: null, order: "desc" });
      sortConfigRef.current = { field: null, order: "desc" };
      setWorkspaceFilters({});
      workspaceFiltersRef.current = {};
      setWorkspaceCompaniesCount(activeWorkspace.companies_count || 0);
      await fetchWorkspaceColumns(activeWorkspaceId);
      await fetchWorkspaceCompanies(activeWorkspaceId, "", false, 0, null, "desc", {});
    };

    loadWorkspaceGridData();
  }, [activeWorkspace, fetchWorkspaceCompanies, fetchWorkspaceColumns]);

  useEffect(() => {
    setColumnEnrichMenuKey(null);
    setActiveEnrichedContactCell(null);
    setEnrichmentOverrides({});
    setLastUsedHsCode("");
    setActiveTradingPartnersCell(null);
    setActiveTradingCountriesCell(null);
    setActiveCompetitorsCell(null);
    setTradingPartnersMap({});
  }, [activeWorkspace]);

  useEffect(() => {
    if (previousDebouncedSearchRef.current === debouncedSearchTerm) return;
    previousDebouncedSearchRef.current = debouncedSearchTerm;
    const activeWorkspaceId = activeWorkspace?.id;
    if (!activeWorkspaceId) return;
    setSelectedRows([]);
    setClearSelection(true);
    setEnrichmentOverrides({});
    setPageNumber(1);
    if (debouncedSearchTerm) {
      trademoTrackingEvents(trackingEventDictionary.workspaceSearch, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), search_term: debouncedSearchTerm, workspace_id: activeWorkspaceId });
    }
    fetchWorkspaceCompanies(activeWorkspaceId, debouncedSearchTerm, false, 0, sortConfigRef.current.field, sortConfigRef.current.order, workspaceFiltersRef.current);
  }, [debouncedSearchTerm, activeWorkspace, fetchWorkspaceCompanies]);

  useEffect(() => {
    const container = tableWrapperRef.current?.querySelector(".new-trademo-table-container");
    if (!container) return;
    const onScroll = () => {
      if (container.scrollLeft > 50) sidebarSetterRef.current?.(false);
    };
    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [isCompaniesLoading, isColumnsLoading]);

  const configuredColumns = useMemo(() => workspaceColumns.filter((column) => column?.is_visible !== false && column?.visible !== false), [workspaceColumns]);
  const hasConfiguredEnrichmentColumns = useMemo(() => configuredColumns.some((column) => column?.column_kind === "enrichment"), [configuredColumns]);

  const scrollToFirstEnrichmentColumn = useCallback(() => {
    const container = tableWrapperRef.current?.querySelector(".new-trademo-table-container") as HTMLElement | null;
    if (!container) return;
    const STICKY_KEYS = new Set<string>([WorkspaceColumnKey.CompanyName]);
    const parseW = (w) => (typeof w === "number" ? w : parseInt(w) || 200);
    let scrollPosition = 40;
    for (const column of configuredColumns) {
      if (column?.column_kind === "enrichment") break;
      if (!STICKY_KEYS.has(column?.column_key)) {
        const key = column?.column_key;
        const minW = COLUMN_WIDTHS[key];
        const w = minW !== null && minW !== undefined ? (key === WorkspaceColumnKey.CompanyCompetitors ? minW : Math.max(parseW(column?.width), minW)) : parseW(column?.width);
        scrollPosition += w;
      }
    }
    container.scrollTo({ left: scrollPosition, behavior: "smooth" });
  }, [configuredColumns]);

  const hasPricingAgentInView = useMemo(() => configuredColumns.some((column) => column?.column_key === WorkspaceColumnKey.PricingAgent), [configuredColumns]);
  const hasAiSummaryInView = useMemo(() => configuredColumns.some((column) => column?.column_key === WorkspaceColumnKey.AiSummary), [configuredColumns]);
  const hasEnrichedContactInView = useMemo(() => configuredColumns.some((column) => column?.column_key === WorkspaceColumnKey.EnrichedContact), [configuredColumns]);
  const hasSanctionsInView = useMemo(() => configuredColumns.some((column) => column?.column_key === WorkspaceColumnKey.SanctionsScreening), [configuredColumns]);
  const hasTraderManufacturerInView = useMemo(() => configuredColumns.some((column) => column?.column_key === WorkspaceColumnKey.TraderManufacturer), [configuredColumns]);
  const hasCompanyEnrichmentInView = useMemo(() => configuredColumns.some((column) => column?.column_kind === "enrichment" && column?.column_key !== WorkspaceColumnKey.PricingAgent && column?.column_key !== WorkspaceColumnKey.EnrichedContact && column?.column_key !== WorkspaceColumnKey.AiSummary && column?.column_key !== WorkspaceColumnKey.SanctionsScreening && column?.column_key !== WorkspaceColumnKey.TraderManufacturer), [configuredColumns]);

  const handleColumnSort = useCallback(
    (columnKey: string, sortType = "number"): void => {
      setSortConfig((prev) => {
        const newConfig: SortConfig = {
          field: columnKey,
          sortType,
          order: prev.field === columnKey && prev.order === "desc" ? "asc" : "desc",
        };
        sortConfigRef.current = newConfig;
        const activeWorkspaceId = activeWorkspaceIdRef.current;
        if (activeWorkspaceId) {
          setPageNumber(1);
          fetchWorkspaceCompanies(activeWorkspaceId, debouncedSearchTermRef.current, false, 0, newConfig.field, newConfig.order, workspaceFiltersRef.current);
        }
        return newConfig;
      });
    },
    [fetchWorkspaceCompanies]
  );

  const handleApplyFilter = (key: FilterKey, value: WorkspaceFiltersValue[FilterKey]) => {
    const nextFilters = { ...workspaceFiltersRef.current, [key]: value };
    workspaceFiltersRef.current = nextFilters;
    setWorkspaceFilters(nextFilters);
    const activeWorkspaceId = activeWorkspace?.id;
    if (activeWorkspaceId) {
      setPageNumber(1);
      fetchWorkspaceCompanies(activeWorkspaceId, debouncedSearchTermRef.current, false, 0, sortConfigRef.current.field, sortConfigRef.current.order, nextFilters);
    }
  };

  const handleClearAllFilters = () => {
    workspaceFiltersRef.current = {};
    setWorkspaceFilters({});
    const activeWorkspaceId = activeWorkspace?.id;
    if (activeWorkspaceId) {
      setPageNumber(1);
      fetchWorkspaceCompanies(activeWorkspaceId, debouncedSearchTermRef.current, false, 0, sortConfigRef.current.field, sortConfigRef.current.order, {});
    }
  };

  const currentPageRows = useMemo(() => {
    const page = Number(pageNumber) || 1;
    const perPage = Number(rowPerPage) || 25;
    const startIndex = Math.max(0, (page - 1) * perPage);
    return workspaceCompanies.slice(startIndex, startIndex + perPage);
  }, [pageNumber, rowPerPage, workspaceCompanies]);

  const fetchTradingPartnersForRow = async (row: WorkspaceRow, limit = 5): Promise<void> => {
    const companyId = row.company_id;
    const companyType = row.company_type;
    const cacheKey = getTradingPartnersCellKey(row);
    if (!companyId || !cacheKey) return;
    setTradingPartnersMap((prev) => ({ ...prev, [cacheKey]: { ...(prev[cacheKey] || {}), loading: true, error: "" } }));
    try {
      const { data } = await axios.get(workspaceApiEndpoints.getIntelWorkspaceTradingPartners, {
        params: {
          company_id: companyId,
          company_type: companyType,
          workspace_id: activeWorkspace?.id,
          limit,
        },
      });
      const normalized = normalizeTradingPartnersPayload(data);
      setTradingPartnersMap((prev) => ({
        ...prev,
        [cacheKey]: {
          loading: false,
          error: "",
          limit,
          partners: normalized.partners,
          totalCount: normalized.totalCount,
        },
      }));
    } catch (error) {
      captureException(error);
      setTradingPartnersMap((prev) => ({
        ...prev,
        [cacheKey]: {
          ...(prev[cacheKey] || {}),
          loading: false,
          error: "Unable to fetch trading partners",
        },
      }));
    }
  };
  const renderTradingPartnersCell = (row: WorkspaceRow, value: string | number | undefined): ReactNode => {
    const cacheKey = getTradingPartnersCellKey(row);
    if (!cacheKey) return <span>{value || "-"}</span>;
    const isOpen = activeTradingPartnersCell === cacheKey;
    const currentData = tradingPartnersMap?.[cacheKey] || {};
    const numericValue = Number(value);
    const countLabel = Number.isNaN(numericValue) ? value || "-" : numericValue.toLocaleString();
    return (
      <div className="tw-relative" ref={isOpen ? tradingPartnersRef : null}>
        {numericValue > 0 ? (
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0 hover:tw-bg-[#FDE68A]"
            onClick={(event) => {
              event.stopPropagation();
              const shouldOpen = !isOpen;
              setActiveTradingCountriesCell(null);
              setActiveTradingPartnersCell(shouldOpen ? cacheKey : null);
              if (shouldOpen) {
                const rect = event.currentTarget.getBoundingClientRect();
                setPopoverAlignRight((prev) => ({ ...prev, tradingPartners: rect.right + 420 > window.innerWidth }));
                if (!currentData?.partners?.length && !currentData?.loading) fetchTradingPartnersForRow(row, 5);
                trademoTrackingEvents(trackingEventDictionary.workspaceTradingPartnersOpen, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row.company_id, workspace_id: activeWorkspaceIdRef.current });
              }
            }}>
            {countLabel}
          </button>
        ) : (
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0">{countLabel}</span>
        )}
        {isOpen && (
          <div className={`tw-absolute tw-top-[calc(100%+10px)] ${popoverAlignRight.tradingPartners ? "tw-right-0" : "tw-left-0"} tw-z-10 tw-w-[420px] tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg`}>
            {currentData?.loading ? (
              <div className="tw-p-2 tw-text-sm tw-text-gray-500">Loading trading partners...</div>
            ) : currentData?.error ? (
              <div className="tw-p-2 tw-text-sm tw-text-red-500">{currentData.error}</div>
            ) : (
              <>
                <div className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-200 tw-border-b tw-border-gray-200">
                  {(currentData?.partners || []).map((partner) => {
                    return (
                      <div key={partner.id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-2">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                          <span className="tw-inline-flex tw-shrink-0 tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-semibold tw-text-[#6941C6]" style={{ backgroundColor: getAvatarBg(partner.name) }}>
                            {(partner.name || "").slice(0, 1).toUpperCase()}
                          </span>
                          <span className="tw-text-sm tw-text-gray-800 tw-truncate tw-capitalize">{partner.name}</span>
                        </div>
                        {partner.shipments ? (
                          <span className="tw-shrink-0 tw-text-sm tw-font-medium tw-text-gray-900">
                            {Number(partner.shipments).toLocaleString()} <span className="tw-text-xs tw-font-normal">shipments</span>
                          </span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {(currentData?.totalCount || 0) > (currentData?.partners || []).length && (
                  <button
                    className="tw-mt-1 tw-px-3 tw-pb-2 tw-w-full tw-flex tw-items-center tw-gap-1 tw-text-left tw-text-sm tw-text-primary-purple-600 tw-border-0 tw-bg-transparent hover:tw-text-primary-purple-700"
                    onClick={(event) => {
                      event.stopPropagation();
                      trademoTrackingEvents(trackingEventDictionary.workspaceTradingPartnersViewAll, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row.company_id, workspace_id: activeWorkspaceIdRef.current });
                      setActiveTradingPartnersCell(null);
                      getSlugHandler(row, {
                        tab: getCompanyProfileTabByType(row),
                        subTab: "trading_partner",
                      });
                    }}>
                    <span className="material-symbols-outlined tw-text-base">open_in_new</span>
                    View All
                  </button>
                )}
                {!currentData?.partners?.length && <div className="tw-p-2 tw-text-sm tw-text-gray-500">No trading partners found.</div>}
              </>
            )}
          </div>
        )}
      </div>
    );
  };
  const renderTradingCountriesCell = (row: WorkspaceRow): ReactNode => {
    const cacheKey = getWorkspaceRowKey(row);
    const countries = Array.isArray(row?.trading_countries) ? row.trading_countries : [];
    const countLabel = countries.length.toLocaleString();
    const isOpen = activeTradingCountriesCell === cacheKey;

    if (!cacheKey) return <span>-</span>;

    return (
      <div className="tw-relative" ref={isOpen ? tradingCountriesRef : null}>
        {countries.length > 0 ? (
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0 hover:tw-bg-[#FDE68A]"
            onClick={(event) => {
              event.stopPropagation();
              setActiveTradingPartnersCell(null);
              if (!isOpen) {
                const rect = event.currentTarget.getBoundingClientRect();
                setPopoverAlignRight((prev) => ({ ...prev, tradingCountries: rect.right + 420 > window.innerWidth }));
                trademoTrackingEvents(trackingEventDictionary.workspaceTradingCountriesOpen, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row.company_id, workspace_id: activeWorkspaceIdRef.current });
              }
              setActiveTradingCountriesCell(isOpen ? null : cacheKey);
            }}>
            {countLabel}
          </button>
        ) : (
          <span>-</span>
        )}
        {isOpen && (
          <div className={`tw-absolute tw-top-[calc(100%+10px)] ${popoverAlignRight.tradingCountries ? "tw-right-0" : "tw-left-0"} tw-z-10 tw-w-[420px] tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg`}>
            {countries.length > 0 ? (
              <div className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-200 tw-max-h-[280px] tw-overflow-y-auto">
                {countries.map((country) => (
                  <div key={country.country} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2 tw-py-2">
                    <span className="tw-text-sm tw-text-gray-800 tw-capitalize tw-truncate">
                      <ReactCountryFlag svg countryCode={country.country_code} />
                      &nbsp;{country.country || "-"}
                    </span>
                    {country.count ? (
                      <span className="tw-shrink-0 tw-text-sm tw-font-medium tw-text-gray-900">
                        {Number(country.count).toLocaleString()} <span className="tw-text-xs tw-font-normal">shipments</span>
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-p-2 tw-text-sm tw-text-gray-500">No trading countries found.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderCompetitorsCell = (row: WorkspaceRow): ReactNode => {
    const cacheKey = getWorkspaceRowKey(row);
    const competitorsNode = row?.company_competitors || {};
    const competitors = Array.isArray(competitorsNode?.data) ? competitorsNode.data : [];
    const count = competitorsNode?.count ?? competitors.length;
    const isOpen = activeCompetitorsCell === cacheKey;

    if (!cacheKey) return <span>-</span>;

    return (
      <div className="tw-relative" ref={isOpen ? competitorsRef : null}>
        {count > 0 ? (
          <button
            className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[34px] tw-h-6 tw-bg-[#FEF3C7] tw-text-[#7A5D00] tw-rounded-full tw-px-2 tw-text-xs tw-font-semibold tw-border-0 hover:tw-bg-[#FDE68A]"
            onClick={(event) => {
              event.stopPropagation();
              setActiveTradingPartnersCell(null);
              setActiveTradingCountriesCell(null);
              if (!isOpen) {
                const rect = event.currentTarget.getBoundingClientRect();
                setPopoverAlignRight((prev) => ({ ...prev, competitors: rect.right + 300 > window.innerWidth }));
                trademoTrackingEvents(trackingEventDictionary.workspaceCompetitorsOpen, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row.company_id, workspace_id: activeWorkspaceIdRef.current });
              }
              setActiveCompetitorsCell(isOpen ? null : cacheKey);
            }}>
            {count.toLocaleString()}
          </button>
        ) : row.is_company_enriched ? (
          <span className="tw-text-gray-400">Not found</span>
        ) : (
          <span className="tw-text-gray-500 tw-text-sm">-</span>
        )}
        {isOpen && (
          <div className={`tw-absolute tw-top-[calc(100%+10px)] ${popoverAlignRight.competitors ? "tw-right-0" : "tw-left-0"} tw-z-10 tw-w-[300px] tw-rounded-2xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg`}>
            {competitors.length > 0 ? (
              <div className="tw-flex tw-flex-col tw-divide-y tw-divide-gray-200 tw-max-h-[280px] tw-overflow-y-auto">
                {competitors.map((name) => (
                  <div key={name} className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                    <span className="tw-inline-flex tw-shrink-0 tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-text-[10px] tw-font-semibold tw-text-[#6941C6]" style={{ backgroundColor: getAvatarBg(name) }}>
                      {(name || "").slice(0, 1).toUpperCase()}
                    </span>
                    <span className="tw-text-sm tw-text-gray-800 tw-truncate tw-capitalize">{name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tw-p-2 tw-text-sm tw-text-gray-500">No competitors found.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const formatCellValueByDataType = (value: unknown, dataType: string | undefined): ReactNode => {
    if (value === undefined || value === null || value === "") return "-";
    const v = value as ReactNode;
    const normalizedType = (dataType || "").toLowerCase();
    if (normalizedType === "number") {
      const numericValue = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
      return Number.isNaN(numericValue) ? v : commaFormat(numericValue);
    }
    if (normalizedType === "date") {
      const parsedDate = moment(value as string);
      if (!parsedDate.isValid()) return v;
      return parsedDate.format("MMM DD, YYYY");
    }
    if (normalizedType === "url") {
      return (
        <a href={value as string} target="_blank" rel="noopener noreferrer" className="tw-text-primary-purple-600 !tw-underline tw-decoration-dashed tw-underline-offset-2 tw-decoration-primary-purple-200 hover:tw-text-primary-purple-700">
          {v}
        </a>
      );
    }
    return v;
  };

  const getCompanyProfileTabByType = (row: WorkspaceRow): "E" | "I" => {
    const normalizedType = String(row.company_type || "")
      .toLowerCase()
      .trim();
    if (normalizedType === "supplier" || normalizedType === "shipper" || normalizedType === "export" || normalizedType === "e") {
      return "E";
    }
    return "I";
  };
  const getSlugHandler = async (row: WorkspaceRow, options: SlugOptions = {}): Promise<void> => {
    const companyId = row?.company_id;
    if (!companyId) return;
    try {
      const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params: { companyId } });
      if (data?.status === "success" && data?.data?.companySlug) {
        const urlParams = new URLSearchParams(window.location.search);
        const baseParams = new URLSearchParams({
          country: urlParams.get("country") || "multicountry",
          type: urlParams.get("type") || "import",
          tab: options?.tab || "I",
        });
        if (options?.subTab) {
          baseParams.set("subTab", options.subTab);
        }
        trademoTrackingEvents(trackingEventDictionary.workspaceCompanyProfileClick, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: companyId, workspace_id: activeWorkspaceIdRef.current });
        const url = getIntelUrl() + data.data.companySlug + "/" + data.data.aliasCompanyId + `?${baseParams.toString()}`;
        window.open(url, "_blank");
      } else {
        toast.error("No company profile found in our system !!!");
      }
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Unable to open company profile"));
    }
  };

  const postEnrichmentForCompanies = useCallback(
    async (rows: WorkspaceRow[], { hsCode = "", productKeyword = "", contactParams = null, companyInfo = false }: { hsCode?: string; productKeyword?: string; contactParams?: ContactEnrichmentParams | null; companyInfo?: boolean } = {}): Promise<void> => {
      if (!hasConfiguredEnrichmentColumns) {
        toast.error("No enrichment columns configured for this workspace.");
        return;
      }
      const targetRows = Array.isArray(rows) ? rows : currentPageRows;
      const nextEnrichingRowKeys = targetRows.map(getWorkspaceRowKey).filter(Boolean);
      const companies = targetRows.map(mapWorkspaceRowToEnrichmentCompany).filter((item) => item.company_name);
      if (companies.length === 0) {
        toast.error("No companies to enrich.");
        return;
      }
      const workspaceId = activeWorkspaceIdRef.current;
      const payload = {
        workspace_id: workspaceId,
        companies,
        hs_code: hsCode,
        ...(contactParams ? { contact_params: contactParams } : {}),
      };
      const pricingPayload = {
        hs_code: hsCode,
        ...(productKeyword ? { product_keyword: productKeyword } : {}),
        workspace_id: workspaceId,
        companies: companies.map((company) => ({
          company_id: company.company_id,
          company_type: company.company_type,
        })),
      };
      const aiSummaryPayload = {
        workspace_id: workspaceId,
        companies: companies.map((company) => ({
          company_id: company.company_id,
          company_type: company.company_type,
        })),
      };
      trademoTrackingEvents(trackingEventDictionary.workspaceEnrichSubmit, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: workspaceId, companies_count: companies.length, scope: Array.isArray(rows) ? "selected" : "page", has_pricing_agent: hasPricingAgentInView, has_ai_summary: hasAiSummaryInView, hs_code: hsCode || null });
      toast.info(`Enriching ${companies.length} ${companies.length === 1 ? "company" : "companies"}. Check the enrichment columns on the right.`, { autoClose: 4000 });
      scrollToFirstEnrichmentColumn();
      const shouldRunGeneralEnrichment = !!(contactParams || companyInfo);
      if (shouldRunGeneralEnrichment) {
        setIsEnriching(true);
        if (contactParams) setContactEnrichingRowKeys(nextEnrichingRowKeys);
        if (companyInfo) setCompanyInfoEnrichingRowKeys(nextEnrichingRowKeys);
      }
      if (hasPricingAgentInView && (hsCode || productKeyword)) setPricingLoadingRowKeys(nextEnrichingRowKeys);
      if (hasAiSummaryInView) setAiSummaryLoadingRowKeys(nextEnrichingRowKeys);

      const applyPricingResponseToRows = (responseData) => {
        const dataNode = responseData?.data && typeof responseData.data === "object" ? responseData.data : responseData;
        const entries = Object.entries(dataNode || {});
        if (!entries.length) return;
        setEnrichmentOverrides((prev) => {
          const next = { ...prev };
          for (const [companyId, pricingData] of entries) {
            next[String(companyId)] = { ...(next[String(companyId)] || {}), pricing_agent: pricingData as WorkspaceRow[WorkspaceColumnKey.PricingAgent] };
          }
          return next;
        });
      };

      const applyAiSummaryResponseToRows = (responseData) => {
        const dataNode = responseData?.data || {};
        const entries = Object.entries(dataNode);
        if (!entries.length) return;
        setEnrichmentOverrides((prev) => {
          const next = { ...prev };
          for (const [companyId, summaryHtml] of entries) {
            next[String(companyId)] = { ...(next[String(companyId)] || {}), ai_summary: summaryHtml as string };
          }
          return next;
        });
      };

      const enrichedCompanyIds = companies.map((c) => c.company_id).filter(Boolean);
      const enrichmentPromise = shouldRunGeneralEnrichment
        ? axios
            .post(workspaceApiEndpoints.postEnrichmentCompanies, payload)
            .then(async ({ data }) => {
              if (data?.status?.toLowerCase() === "success") {
                toast.success(data?.message || "Enrichment completed successfully.");
                await fetchWorkspaceCompaniesLookup(workspaceId, enrichedCompanyIds);
              }
            })
            .catch((error) => {
              captureException(error);
              toast.error(getErrorMessage(error, "Enrichment request failed."));
            })
            .finally(() => {
              setIsEnriching(false);
              setContactEnrichingRowKeys([]);
              setCompanyInfoEnrichingRowKeys([]);
            })
        : Promise.resolve();

      const pricingPromise =
        hasPricingAgentInView && (hsCode || productKeyword)
          ? axios
              .post(workspaceApiEndpoints.pricingAgent, pricingPayload)
              .then(({ data }) => applyPricingResponseToRows(data))
              .catch((error) => {
                captureException(error);
                toast.error(getErrorMessage(error, "Pricing enrichment failed."));
              })
              .finally(() => setPricingLoadingRowKeys([]))
          : Promise.resolve();

      const aiSummaryPromise = hasAiSummaryInView
        ? axios
            .post(workspaceApiEndpoints.aiSummary, aiSummaryPayload)
            .then(({ data }) => applyAiSummaryResponseToRows(data))
            .catch((error) => {
              captureException(error);
              toast.error(getErrorMessage(error, "AI summary enrichment failed."));
            })
            .finally(() => setAiSummaryLoadingRowKeys([]))
        : Promise.resolve();

      await Promise.allSettled([enrichmentPromise, pricingPromise, aiSummaryPromise]);
      (dispatch as (action: unknown) => void)(getCreditsByPlanHandler());
    },
    [currentPageRows, fetchWorkspaceCompaniesLookup, hasConfiguredEnrichmentColumns, hasPricingAgentInView, hasAiSummaryInView, scrollToFirstEnrichmentColumn, dispatch]
  );

  // Sanctions screening is a standalone, explicit action (never bundled into
  // "enrich all"). Always re-calls the API; results persist in Postgres so a
  // re-visit renders the stored result without re-screening.
  const runSanctionsScreening = useCallback(
    async (rows: WorkspaceRow[]): Promise<void> => {
      const targetRows = Array.isArray(rows) && rows.length ? rows : currentPageRows;
      if (targetRows.length > 50) {
        toast.error("You can screen a maximum of 50 companies at a time.");
        return;
      }
      const companies = targetRows
        .map((row) => ({
          company_id: String(row?.company_id ?? ""),
          company_name: String(row?.company_name ?? ""),
          company_country: String(row?.country ?? ""),
        }))
        .filter((company) => company.company_id && company.company_name);
      if (companies.length === 0) {
        toast.error("No companies to screen.");
        return;
      }
      const workspaceId = activeWorkspaceIdRef.current;
      const rowKeys = targetRows.map(getWorkspaceRowKey).filter(Boolean);
      trademoTrackingEvents(trackingEventDictionary.workspaceEnrichSubmit, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: workspaceId, companies_count: companies.length, scope: Array.isArray(rows) && rows.length ? "selected" : "page", enrichment_type: "sanctions_screening" });
      setSanctionsLoadingRowKeys(rowKeys);
      toast.info(`Screening ${companies.length} ${companies.length === 1 ? "company" : "companies"} for sanctions. Check the Sanctions screening column.`, { autoClose: 4000 });
      scrollToFirstEnrichmentColumn();
      try {
        const { data } = await axios.post(workspaceApiEndpoints.sanctionsScreening, {
          workspace_id: workspaceId,
          companies,
        });
        const dataNode = data?.data && typeof data.data === "object" ? data.data : {};
        const entries = Object.entries(dataNode);
        if (entries.length) {
          setEnrichmentOverrides((prev) => {
            const next = { ...prev };
            for (const [companyId, result] of entries) {
              next[String(companyId)] = { ...(next[String(companyId)] || {}), sanctions_screening: result as SanctionsScreeningResult, is_sanctions_enriched: true };
            }
            return next;
          });
        }
      } catch (error) {
        captureException(error);
        toast.error(getErrorMessage(error, "Sanctions screening failed."));
      } finally {
        setSanctionsLoadingRowKeys([]);
        (dispatch as (action: unknown) => void)(getCreditsByPlanHandler());
      }
    },
    [currentPageRows, scrollToFirstEnrichmentColumn, dispatch]
  );

  const runTraderManufacturer = useCallback(
    async (rows: WorkspaceRow[]): Promise<void> => {
      const targetRows = Array.isArray(rows) && rows.length ? rows : currentPageRows;
      if (targetRows.length > 50) {
        toast.error("You can classify a maximum of 50 companies at a time.");
        return;
      }
      const companies = targetRows
        .map((row) => ({
          company_id: String(row?.company_id ?? ""),
          company_name: String(row?.company_name ?? ""),
          company_country: String(row?.country ?? ""),
        }))
        .filter((company) => company.company_id && company.company_name);
      if (companies.length === 0) {
        toast.error("No companies to classify.");
        return;
      }
      const workspaceId = activeWorkspaceIdRef.current;
      const rowKeys = targetRows.map(getWorkspaceRowKey).filter(Boolean);
      trademoTrackingEvents(trackingEventDictionary.workspaceEnrichSubmit, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: workspaceId, companies_count: companies.length, scope: Array.isArray(rows) && rows.length ? "selected" : "page", enrichment_type: "trader_manufacturer" });
      setTraderManufacturerLoadingRowKeys((prev) => [...new Set([...prev, ...rowKeys])]);
      scrollToFirstEnrichmentColumn();
      try {
        const { data } = await axios.post(workspaceApiEndpoints.traderManufacturer, {
          workspace_id: workspaceId,
          companies,
        });
        const dataNode = data?.data && typeof data.data === "object" ? data.data : {};
        const entries = Object.entries(dataNode);
        if (entries.length) {
          setEnrichmentOverrides((prev) => {
            const next = { ...prev };
            for (const [companyId, result] of entries) {
              next[String(companyId)] = { ...(next[String(companyId)] || {}), trader_manufacturer: String(result ?? ""), is_trader_manufacturer_enriched: true };
            }
            return next;
          });
        }
      } catch (error) {
        captureException(error);
        toast.error(getErrorMessage(error, "Trader/Manufacturer classification failed."));
      } finally {
        setTraderManufacturerLoadingRowKeys((prev) => prev.filter((key) => !rowKeys.includes(key)));
      }
    },
    [currentPageRows, scrollToFirstEnrichmentColumn]
  );

  const requestEnrichmentForRows = useCallback((rows: WorkspaceRow[]): void => {
    if (rows?.length > 50) {
      toast.error("You can enrich a maximum of 50 companies at a time.");
      return;
    }
    setPendingEnrichmentRows(rows);
    setShowEnrichmentInputsModal(true);
  }, []);

  const handleEnrichmentInputsSubmit = useCallback(
    async ({ hsCode = "", productKeyword = "", contactParams = null, companyInfo = false, sanctions = false, traderManufacturer = false }: { hsCode?: string; productKeyword?: string; contactParams?: ContactEnrichmentParams | null; companyInfo?: boolean; sanctions?: boolean; traderManufacturer?: boolean }) => {
      const rows = Array.isArray(pendingEnrichmentRows) ? pendingEnrichmentRows : selectedRows.length > 0 ? selectedRows : currentPageRows;
      setShowEnrichmentInputsModal(false);
      setPendingEnrichmentRows(null);
      if (hsCode) setLastUsedHsCode(hsCode);
      const hasOtherEnrichment = !!(hsCode || productKeyword || contactParams || companyInfo);
      await Promise.allSettled([...(sanctions ? [runSanctionsScreening(rows)] : []), ...(traderManufacturer ? [runTraderManufacturer(rows)] : []), ...(hasOtherEnrichment ? [postEnrichmentForCompanies(rows, { hsCode, productKeyword, contactParams, companyInfo })] : [])]);
    },
    [pendingEnrichmentRows, selectedRows, currentPageRows, postEnrichmentForCompanies, runSanctionsScreening, runTraderManufacturer]
  );

  const openAiSummaryModal = useCallback((row: WorkspaceRow): void => {
    const summary = row?.ai_summary;
    if (!summary || !String(summary).trim()) {
      toast.error("AI summary not available yet.");
      return;
    }
    trademoTrackingEvents(trackingEventDictionary.workspaceAiSummaryView, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row?.company_id, company_name: row?.company_name, workspace_id: activeWorkspaceIdRef.current });
    setAiSummaryCompanyName(row?.company_name || "Company");
    setActiveAiSummaryHtml(String(summary));
    setShowAiSummaryModal(true);
  }, []);

  const openPricingPane = useCallback((row: WorkspaceRow): void => {
    const pricing = row?.pricing_agent;
    if (!Array.isArray(pricing) || !pricing.length) {
      toast.error("Pricing not available yet.");
      return;
    }
    setPricingCompanyName(row?.company_name || "Company");
    setActivePricing(pricing as PricingAgentItem[]);
    setShowPricingPane(true);
  }, []);

  const openSanctionsPane = useCallback((row: WorkspaceRow): void => {
    const sanctions = row?.sanctions_screening;
    const matches = sanctions && !sanctions.error ? sanctions.matches || [] : [];
    setSanctionsCompanyName(row?.company_name || "Company");
    setActiveSanctions(matches);
    setShowSanctionsPane(true);
  }, []);

  const centerEllipsis = (str: string | undefined | null, maxLen = 32): string => {
    if (!str || str.length <= maxLen) return str;
    const half = Math.floor((maxLen - 3) / 2);
    return str.slice(0, half) + "…" + str.slice(str.length - (maxLen - 3 - half));
  };

  const openSourcesModal = useCallback((companyId: string) => {
    setSourcesModalCompanyId(companyId);
    setSourcesData(null);
    setSourcesError(null);
    setSourcesLoading(true);
    axios
      .post(workspaceApiEndpoints.enrichmentSources, { company_ids: [companyId] })
      .then(({ data }) => setSourcesData(data))
      .catch((err) => {
        captureException(err);
        setSourcesError("Failed to load sources.");
      })
      .finally(() => setSourcesLoading(false));
  }, []);

  const tableColumns = useMemo(() => {
    const STICKY_KEYS = new Set<string>([WorkspaceColumnKey.CompanyName]);
    const parseW = (w) => (typeof w === "number" ? w : parseInt(w) || 200);
    let stickyOffset = 40; // checkbox column width

    const workspaceCriteriaAvailable = hasWorkspaceListCriteria(activeWorkspace?.search_payload ?? activeWorkspace?.filters_json);

    const cols = configuredColumns.map((column) => {
      const { column_kind, column_key, data_type } = column;
      const showEnrichmentIcon = column_kind === "enrichment";
      const showInfoIcon = workspaceCriteriaAvailable && INFO_ICON_COLUMNS.has(column_key);
      const isRightAlign = RIGHT_ALIGN_LABELS.has(column_key);
      const isDateSort = DATE_SORT_LABELS.has(column_key);
      const isPricingAgentColumn = column_key === WorkspaceColumnKey.PricingAgent;
      const isTradingPartnersColumn = column_key === WorkspaceColumnKey.TradingPartners;
      const isTradingCountriesColumn = column_key === WorkspaceColumnKey.TradingCountries;
      const isCompetitorsColumn = column_key === WorkspaceColumnKey.CompanyCompetitors;
      const isCenterAlign = isTradingPartnersColumn || isTradingCountriesColumn || isCompetitorsColumn;
      const isSticky = STICKY_KEYS.has(column_key);
      const minW = COLUMN_WIDTHS[column_key];
      const minColWidth = minW !== null && minW !== undefined ? (column_key === WorkspaceColumnKey.CompanyCompetitors ? minW : Math.max(parseW(column?.width), minW)) : parseW(column?.width);
      const leftOffset = stickyOffset;
      if (isSticky) stickyOffset += minColWidth;
      const isFixedWidth = column_key === WorkspaceColumnKey.CompanyDescription || column_key === WorkspaceColumnKey.CompanyCompetitors;
      const stickyProps = isSticky ? { isSticky: "start", left: `${leftOffset}px`, miWidth: minColWidth, maWidth: minColWidth } : { miWidth: minColWidth, maWidth: isFixedWidth ? minColWidth : "2000px" };

      return {
        name: (
          <div className={`tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-1.5 ${isCenterAlign ? "tw-justify-center" : "tw-justify-between"} ${isRightAlign || isDateSort ? "tw-cursor-pointer tw-select-none" : ""}`} onClick={isRightAlign ? () => handleColumnSort(column_key, "number") : isDateSort ? () => handleColumnSort(column_key, "date") : undefined}>
            <span className="tw-flex tw-items-center tw-gap-1">
              {column?.display_label || column_key}
              {(isRightAlign || isDateSort) && <span className="material-symbols-outlined tw-text-sm tw-leading-none tw-text-gray-400">{sortConfig.field === column_key ? (sortConfig.order === "asc" ? "arrow_upward" : "arrow_downward") : "unfold_more"}</span>}
            </span>
            <span className="tw-flex tw-items-center tw-gap-1">
              {showEnrichmentIcon ? (
                <span className="tw-cursor-default" data-tooltip-id="workspace-tooltip" data-tooltip-content={enrichmentColumnTooltips[column_key as WorkspaceColumnKey] ?? "This is an AI enriched attribute. Trademo enriches content with data from various open source proprietary data-sets."}>
                  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#fa750f">
                    <path d="m288-96 144-288-288-48 456-432h72L528-576l288 48L360-96h-72Z" />
                  </svg>
                </span>
              ) : null}

              {showInfoIcon ? (
                <span
                  className="material-symbols-outlined tw-text-base tw-leading-none tw-text-gray-400 tw-cursor-pointer hover:tw-text-primary-purple-600"
                  data-tooltip-id="workspace-tabs-tooltip"
                  data-tooltip-content="View list creation criteria"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCriteriaModal(true);
                  }}>
                  info
                </span>
              ) : null}
              {isPricingAgentColumn && lastUsedHsCode ? (
                <span className="material-symbols-outlined tw-text-base tw-leading-none tw-text-gray-400 tw-cursor-default" data-tooltip-id="workspace-tabs-tooltip" data-tooltip-content={`Pricing Insights for HS Code ${lastUsedHsCode} Over the Last 2 Years`}>
                  info
                </span>
              ) : null}
            </span>
          </div>
        ),
        selector: (row) => {
          const rowKey = getWorkspaceRowKey(row);
          const override = enrichmentOverrides[rowKey];
          const eRow = override ? { ...row, ...override } : row;
          const enrichingBadge = (text: string) => (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-violet-200 tw-bg-violet-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
              <span className="material-symbols-outlined tw-text-xs tw-animate-pulse">auto_awesome</span>
              {text}
            </span>
          );

          if (isTradingPartnersColumn) return <div className="tw-flex tw-justify-center">{renderTradingPartnersCell(eRow, eRow?.[column_key])}</div>;
          if (isTradingCountriesColumn) return <div className="tw-flex tw-justify-center">{renderTradingCountriesCell(eRow)}</div>;
          if (isCompetitorsColumn) {
            if (companyInfoEnrichingRowKeys.includes(rowKey)) {
              return <div className="tw-flex tw-justify-center">{enrichingBadge("Enriching...")}</div>;
            }
            return <div className="tw-flex tw-justify-center">{renderCompetitorsCell(eRow)}</div>;
          }
          if (column_key === WorkspaceColumnKey.CompanyName) {
            return (
              <div className="tw-flex tw-flex-col tw-gap-0.5 tw-min-w-0 tw-w-full">
                <button type="button" data-tooltip-id={eRow?.company_name?.length > 35 ? "workspace-tooltip" : ""} data-tooltip-content={eRow?.company_name} className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-underline tw-decoration-dashed tw-underline-offset-2 tw-decoration-gray-400 tw-cursor-pointer tw-capitalize tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-block tw-w-full tw-text-primary-purple-600" onClick={() => getSlugHandler(eRow, { tab: getCompanyProfileTabByType(eRow) })}>
                  {centerEllipsis(eRow?.company_name || "-")}
                </button>
                <div className="tw-flex tw-items-center tw-gap-1 tw-text-gray-500 tw-capitalize tw-overflow-hidden tw-text-xs">
                  {eRow.country_code ? <ReactCountryFlag countryCode={eRow.country_code} svg className="tw-flex-shrink-0" /> : null}
                  {eRow.country && <span className="tw-whitespace-nowrap tw-shrink-0">{eRow.country}</span>}
                  {eRow.full_address && (
                    <span data-tooltip-id={eRow.full_address?.length > 35 ? "workspace-tooltip" : ""} data-tooltip-content={eRow.full_address} className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-shrink">
                      {`${eRow.country_code || eRow.country ? "| " : ""}${centerEllipsis(eRow.full_address)}`}
                    </span>
                  )}
                </div>
              </div>
            );
          }

          if (column_key === WorkspaceColumnKey.AiSummary) {
            const isAiSummaryLoading = aiSummaryLoadingRowKeys.includes(rowKey);
            if (isAiSummaryLoading) {
              return (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-violet-200 tw-bg-violet-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                  <span className="material-symbols-outlined tw-text-xs tw-animate-pulse">auto_awesome</span>
                  AI summarizing...
                </span>
              );
            }
            if (typeof eRow?.ai_summary === "string" && eRow.ai_summary.trim()) {
              return (
                <button
                  type="button"
                  className="tw-h-7 tw-rounded-md tw-border tw-border-primary-purple-500 tw-bg-white tw-px-2.5 tw-text-xs tw-font-medium tw-text-primary-purple-600 hover:tw-bg-primary-purple-50"
                  onClick={(event) => {
                    event.stopPropagation();
                    openAiSummaryModal(eRow);
                  }}>
                  View Summary
                </button>
              );
            }
            return "-";
          }

          if (column_key === WorkspaceColumnKey.PricingAgent) {
            const isPricingLoading = pricingLoadingRowKeys.includes(rowKey);
            if (isPricingLoading) {
              return (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-violet-200 tw-bg-violet-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                  <span className="material-symbols-outlined tw-text-xs tw-animate-pulse">auto_awesome</span>
                  Enriching...
                </span>
              );
            }
            const pricingAgent = eRow?.pricing_agent;
            if (!pricingAgent) return eRow?.is_pricing_enriched ? <span className="tw-text-gray-400">Not found</span> : "-";
            if (typeof pricingAgent === "string") {
              return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-gray-400">{pricingAgent}</span>;
            }
            if (Array.isArray(pricingAgent)) {
              if (!pricingAgent.length) return eRow?.is_pricing_enriched ? <span className="tw-text-gray-400">Not found</span> : "-";
              return (
                <button
                  type="button"
                  className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-500 tw-underline tw-underline-offset-2 tw-cursor-pointer hover:tw-text-primary-purple-700"
                  onClick={(event) => {
                    event.stopPropagation();
                    openPricingPane(eRow);
                  }}>
                  View Pricing
                </button>
              );
            }
            return "-";
          }

          if (column_key === WorkspaceColumnKey.SanctionsScreening) {
            if (sanctionsLoadingRowKeys.includes(rowKey)) {
              return (
                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-violet-200 tw-bg-violet-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-violet-700">
                  <span className="material-symbols-outlined tw-text-xs tw-animate-pulse">auto_awesome</span>
                  Screening...
                </span>
              );
            }
            const sanctions = eRow?.sanctions_screening;
            if (!sanctions || typeof sanctions !== "object" || sanctions.error) {
              return eRow?.is_sanctions_enriched ? <span className="tw-text-gray-400">Not screened</span> : "-";
            }
            const sanctionsCount = typeof sanctions.count === "number" ? sanctions.count : sanctions.matches?.length || 0;
            if (sanctionsCount > 0) {
              return (
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-red-600 tw-cursor-pointer hover:tw-text-red-700"
                  onClick={(event) => {
                    event.stopPropagation();
                    openSanctionsPane(eRow);
                  }}>
                  <span className="material-symbols-outlined tw-text-base tw-leading-none">error</span>
                  <span className="tw-underline">{`${sanctionsCount} ${sanctionsCount === 1 ? "sanction" : "sanctions"} found`}</span>
                </button>
              );
            }
            return (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-green-600">
                <span className="material-symbols-outlined tw-text-base tw-leading-none">check_circle</span>
                No sanctions found
              </span>
            );
          }

          if (column_key === WorkspaceColumnKey.TraderManufacturer) {
            if (traderManufacturerLoadingRowKeys.includes(rowKey)) return enrichingBadge("Classifying...");
            const tm = eRow?.trader_manufacturer;
            const label = typeof tm === "string" ? tm : "";
            if (!label) return eRow?.is_trader_manufacturer_enriched ? <span className="tw-text-gray-400">Not found</span> : "-";
            return <span className={`tw-capitalize ${/^unable to determine$/i.test(label.trim()) ? "tw-text-gray-400" : "tw-text-gray-900"}`}>{label}</span>;
          }

          const formattedValue = formatCellValueByDataType(eRow?.[column_key], data_type);
          if (showEnrichmentIcon && column_key !== WorkspaceColumnKey.EnrichedContact && companyInfoEnrichingRowKeys.includes(rowKey)) return enrichingBadge("Enriching...");
          if (showEnrichmentIcon && contactEnrichingRowKeys.includes(rowKey)) return enrichingBadge("Enriching...");

          if (column_key === WorkspaceColumnKey.CompanyIndustry) {
            const industry = eRow?.company_industry;
            const notFound = !industry && eRow?.is_company_enriched;
            const displayIndustry = notFound ? "Not found" : industry || "-";
            return (
              <span className={`${notFound ? "tw-text-gray-400" : "tw-text-gray-900"} tw-capitalize tw-block tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis`} title={displayIndustry}>
                {displayIndustry}
              </span>
            );
          }

          if (column_key === WorkspaceColumnKey.CompanyDescription) {
            const description = eRow?.company_description;
            const notFound = !description && eRow?.is_company_enriched;
            const displayDescription = notFound ? "Not found" : description || "-";
            return (
              <span data-tooltip-id="workspace-tooltip" data-tooltip-content={!notFound && description ? description : undefined} className={`${notFound ? "tw-text-gray-400" : "tw-text-gray-900"} tw-capitalize tw-block tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis`}>
                {displayDescription}
              </span>
            );
          }

          if (column_key === WorkspaceColumnKey.CompanyDomain) {
            const domainStr = eRow?.company_domain;
            if (!domainStr) return eRow?.is_company_enriched ? <span className="tw-text-gray-400">Not found</span> : "-";
            const href = domainStr.startsWith("http") ? domainStr : `https://${domainStr}`;
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" className="tw-text-primary-purple-600 tw-block tw-whitespace-nowrap tw-overflow-hidden tw-decoration-dashed tw-underline tw-underline-offset-2 tw-decoration-primary-purple-200 hover:tw-text-primary-purple-700" title={domainStr} onClick={() => trademoTrackingEvents(trackingEventDictionary.workspaceDomainView, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: eRow?.company_id, workspace_id: activeWorkspaceIdRef.current })}>
                {centerEllipsis(domainStr)}
              </a>
            );
          }

          if (column_key === WorkspaceColumnKey.EnrichedContact) {
            const contacts = eRow?.enriched_contact;
            if (!contacts?.length) return eRow?.is_contact_enriched ? <span className="tw-text-gray-400">Not found</span> : "-";
            const isOpen = activeEnrichedContactCell === row.company_id;
            return (
              <div className="tw-relative tw-inline-flex" ref={isOpen ? enrichedContactsRef : null}>
                <button
                  type="button"
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-gray-900"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (activeEnrichedContactCell !== row.company_id) {
                      trademoTrackingEvents(trackingEventDictionary.workspaceEnrichedContactView, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), company_id: row.company_id, workspace_id: activeWorkspaceIdRef.current });
                    }
                    setActiveEnrichedContactCell((prev) => (prev === row.company_id ? null : row.company_id));
                  }}>
                  <span className="tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100 tw-text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#101828">
                      <path d="M170-72.31v-60h620v60H170Zm0-755.38v-60h620v60H170ZM480-420q45.77 0 77.88-32.11Q590-484.23 590-530t-32.12-77.88Q525.77-640 480-640q-45.77 0-77.88 32.12Q370-575.77 370-530q0 45.77 32.12 77.89Q434.23-420 480-420ZM172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31Zm83.85-60q45-48.31 101.69-74.15Q414.54-340 480-340q65.46 0 122.15 25.85 56.69 25.84 101.69 74.15h83.85q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H172.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h83.85Zm91.84 0h264q-29-20-62.5-30T480-280q-36 0-69.5 10T348-240Zm96.58-254.57Q430-509.15 430-530q0-20.84 14.58-35.42Q459.15-580 480-580t35.42 14.58Q530-550.84 530-530q0 20.85-14.58 35.43Q500.85-480 480-480t-35.42-14.57ZM480-480Z" />
                    </svg>
                  </span>
                  <span>{contacts.length}</span>
                </button>
                {isOpen ? (
                  <div className="tw-absolute tw-right-[calc(100%+8px)] tw-top-0 tw-z-50">
                    <EnrichedContactsPopover contacts={contacts} />
                  </div>
                ) : null}
              </div>
            );
          }
          return <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-gray-900 ${isRightAlign ? "tw-w-full tw-justify-end" : ""}`}>{formattedValue === "-" && showEnrichmentIcon ? eRow?.is_company_enriched ? <span className="tw-text-gray-400">Not found</span> : "-" : <span>{formattedValue}</span>}</span>;
        },
        ...stickyProps,
      };
    });

    if (isInternalUser) {
      cols.push({
        name: <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Sources</span>,
        selector: (row: WorkspaceRow) => (
          <button
            type="button"
            className="tw-h-7 tw-rounded-md tw-border tw-border-primary-purple-500 tw-bg-white tw-px-2.5 tw-text-xs tw-font-medium tw-text-primary-purple-600 hover:tw-bg-primary-purple-50 tw-transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              openSourcesModal(String(row.company_id ?? ""));
            }}>
            View Sources
          </button>
        ),
        miWidth: 130,
        maWidth: 130,
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredColumns, enrichmentOverrides, activeTradingPartnersCell, activeTradingCountriesCell, activeEnrichedContactCell, activeCompetitorsCell, tradingPartnersMap, columnEnrichMenuKey, isEnriching, pricingLoadingRowKeys, aiSummaryLoadingRowKeys, sanctionsLoadingRowKeys, traderManufacturerLoadingRowKeys, contactEnrichingRowKeys, companyInfoEnrichingRowKeys, requestEnrichmentForRows, openAiSummaryModal, openPricingPane, openSanctionsPane, activeWorkspace, sortConfig, handleColumnSort, isInternalUser, openSourcesModal]);
  const isTableLoading = isCompaniesLoading || isColumnsLoading;
  const hasWorkspace = workspaceList.length > 0;
  const hasSearchQuery = debouncedSearchTerm.length > 0;
  const hasActiveFilters = countActiveFilters(workspaceFilters) > 0;
  const selectedCount = selectedRows.length;
  const availableWorkspaceRows = workspaceCompaniesCount || workspaceCompanies.length || 0;
  const hasWorkspaceCompanies = (activeWorkspace.companies_count ?? 0) > 0;

  const handleTrademoTableSearchPerRowsChange = (perPage: number): void => {
    setRowPerPage(perPage);
  };

  const updateTrademoTableObject = (data: TrademoTableState): void => {
    setRowPerPage(data.perPage);
    setPageNumber(data.currentPage + 1);
  };

  const handleTrademoTablePageChange = (page: number, perPage: number): void => {
    setRowPerPage(perPage);
    setPageNumber(page);
    const activeWorkspaceId = activeWorkspace?.id;
    if (!activeWorkspaceId) return;
    const needed = page * perPage;
    if (needed >= workspaceCompanies.length && workspaceCompanies.length < workspaceCompaniesCount) {
      fetchWorkspaceCompanies(activeWorkspaceId, debouncedSearchTermRef.current, true, workspaceCompanies.length, sortConfigRef.current.field, sortConfigRef.current.order);
    }
  };

  const noSelectionTooltipMessage = "To perform this action, you need to select records from the table.";
  const clearSelectedCompanies = () => {
    setSelectedRows([]);
    setShowContactsDrop(false);
    setShowMoreActionsMenu(false);
    setClearSelection(true);
  };

  const handleWorkspaceDownload = async ({ isCustomGridDownload = false, from = 1, to = 1 }: { isCustomGridDownload?: boolean; from?: number | string; to?: number | string } = {}): Promise<void> => {
    const activeWorkspaceId = activeWorkspace?.id;
    if (!activeWorkspaceId) {
      toast.error("No active workspace found");
      return;
    }
    if (selectedCount === 0 && (+to < +from || +from < 1)) {
      toast.error("Please provide a valid download range");
      return;
    }
    setIsDownloading(true);
    try {
      const payload: {
        is_range: boolean;
        rows?: WorkspaceRow[];
        from?: number;
        size?: number;
        columns?: string[];
      } = selectedCount > 0 ? { rows: selectedRows, is_range: false } : { from: +from - 1, size: +to - +from + 1, is_range: true };
      if (isCustomGridDownload) {
        payload.columns = configuredColumns.map((column) => column?.column_key).filter(Boolean);
      } else {
        payload.columns = workspaceColumns.map((column) => column?.column_key).filter(Boolean);
      }
      const { data } = await axios.post(workspaceApiEndpoints.downloadIntelWorkspaceCompanies(activeWorkspaceId), payload);
      if (data?.file_url) {
        window.open(data.file_url, "_blank");
      }
      trademoTrackingEvents(trackingEventDictionary.workspaceDownload, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceId, download_type: selectedCount > 0 ? "selected" : "range", companies_count: selectedCount > 0 ? selectedCount : payload.size });
      toast.success(data?.message || "Workspace download started");
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Unable to download workspace companies"));
    } finally {
      setIsDownloading(false);
    }
  };
  const contactsReqTypes = useMemo(
    () => [
      {
        name: "Employee Level Contacts",
        cb: () => {
          const remainingContactRequests = (userFirstPlan?.contact_max_request || 0) - (userFirstPlan?.contact_current_request || 0);
          if (selectedCount <= remainingContactRequests) {
            trademoTrackingEvents(trackingEventDictionary.workspaceFetchEmployeeContacts, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceIdRef.current, companies_count: selectedCount });
            setShowContactModal(true);
          } else {
            toast.warning(`You can request contacts for ${remainingContactRequests} more companies today.`);
          }
        },
      },
    ],
    [userFirstPlan, selectedCount]
  );

  const renderActionsButton = (disableOptions = false) => (
    <div className="tw-relative" ref={moreActionsRef}>
      <button
        className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5 tw-shadow-button hover:tw-bg-gray-50"
        onClick={() => {
          if (!showMoreActionsMenu) {
            trademoTrackingEvents(trackingEventDictionary.workspaceActions, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspace?.id });
          }
          setShowMoreActionsMenu((prev) => !prev);
        }}>
        <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
          <path d="M479.79-192Q450-192 429-213.21t-21-51Q408-294 429.21-315t51-21Q510-336 531-314.79t21 51Q552-234 530.79-213t-51 21Zm0-216Q450-408 429-429.21t-21-51Q408-510 429.21-531t51-21Q510-552 531-530.79t21 51Q552-450 530.79-429t-51 21Zm0-216Q450-624 429-645.21t-21-51Q408-726 429.21-747t51-21Q510-768 531-746.79t21 51Q552-666 530.79-645t-51 21Z" />
        </svg>
        Actions
      </button>
      {showMoreActionsMenu && (
        <div className="tw-absolute tw-top-[calc(100%+8px)] tw-left-0 tw-z-30 tw-min-w-[170px] tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg" data-tooltip-id="workspace-tooltip" data-tooltip-content={disableOptions ? noSelectionTooltipMessage : ""}>
          <button
            className={`tw-w-full tw-border-0 tw-bg-transparent tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-gap-2 ${disableOptions ? "tw-text-gray-400 tw-cursor-not-allowed" : "hover:tw-bg-gray-50"}`}
            onClick={() => {
              if (disableOptions) return;
              setShowMoreActionsMenu(false);
              setShowFavModal(true);
              trademoTrackingEvents(trackingEventDictionary.workspaceBookmarkClick, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspace?.id, companies_count: selectedRows.length });
            }}>
            <span className="material-symbols-outlined tw-text-base">star</span>
            Bookmark
          </button>
          <button
            className={`tw-w-full tw-border-0 tw-bg-transparent tw-text-left tw-px-3 tw-py-2 tw-text-sm tw-border-t tw-border-gray-100 tw-flex tw-items-center tw-gap-2 ${disableOptions ? "tw-text-gray-400 tw-cursor-not-allowed" : "tw-text-error-500 hover:tw-bg-error-25"}`}
            onClick={() => {
              if (disableOptions) return;
              setShowMoreActionsMenu(false);
              setShowRemoveCompaniesModal(true);
            }}>
            <span className="material-symbols-outlined tw-text-base">delete</span>
            Delete
          </button>
        </div>
      )}
    </div>
  );

  const handleSaveColumnConfiguration = async (updatedColumns: WorkspaceColumn[]): Promise<boolean> => {
    const activeWorkspaceId = activeWorkspace?.id;
    if (!activeWorkspaceId) return false;

    const nextColumns = (() => {
      const normalizedUpdated = Array.isArray(updatedColumns) ? updatedColumns : [];
      if (columnConfigMode === "all") {
        return normalizedUpdated;
      }
      const updatedByKey = new Map(normalizedUpdated.map((column) => [column?.column_key, column]));
      return workspaceColumns.map((column) => {
        const updated = updatedByKey.get(column?.column_key);
        return updated ? { ...column, ...updated } : column;
      });
    })();

    const payload = {
      columns: nextColumns
        .filter((column) => Boolean(column?.column_key))
        .map((column) => {
          const item: { column_key: string | undefined; is_visible: boolean; pinned_left?: boolean; width?: string | number } = {
            column_key: column.column_key,
            is_visible: column?.is_visible !== false,
          };
          if (column?.pinned_left !== undefined) item.pinned_left = column.pinned_left;
          if (column?.width !== undefined && column?.width !== null) item.width = column.width;
          return item;
        }),
    };

    try {
      const { data } = await axios.put(workspaceApiEndpoints.updateIntelWorkspaceColumns(activeWorkspaceId), payload);
      const apiColumns = extractColumnsPayload(data);
      setWorkspaceColumns(apiColumns.length ? apiColumns : nextColumns);
      trademoTrackingEvents(trackingEventDictionary.workspaceColumnConfigureSave, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceId, mode: columnConfigMode, visible_columns_count: payload.columns.filter((c) => c.is_visible).length });
      return true;
    } catch (error) {
      captureException(error);
      return false;
    }
  };

  const handleConfirmDeleteWorkspace = async (): Promise<void> => {
    const deletedWorkspaceId = workspaceToDelete?.id;
    if (!deletedWorkspaceId) return;
    try {
      await axios.delete(workspaceApiEndpoints.deleteIntelWorkspace(deletedWorkspaceId));
      trademoTrackingEvents(trackingEventDictionary.workspaceDeleteConfirm, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: deletedWorkspaceId, workspace_name: workspaceToDelete?.name });
      const remainingWorkspaces = workspaceList.filter((workspace) => workspace.id !== deletedWorkspaceId);
      setWorkspaceList(remainingWorkspaces);
      if (activeWorkspace?.id === deletedWorkspaceId) {
        setActiveWorkspace(remainingWorkspaces[0] || {});
      }
      toast.success("Workspace deleted successfully.");
      setWorkspaceToDelete(null);
      dispatch(getSidebarCountData());
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Failed to delete workspace."));
    }
  };

  const handleConfirmRemoveSelectedCompanies = async (): Promise<void> => {
    const activeWorkspaceId = activeWorkspace?.id;
    const workspaceCompanyIds = selectedRows.map((row) => row.id).filter((id): id is number => id !== undefined);
    if (!activeWorkspaceId || !workspaceCompanyIds.length) return;
    try {
      const { data } = await axios.post(workspaceApiEndpoints.bulkDeleteWorkspaceCompanies(activeWorkspaceId), { workspace_company_ids: workspaceCompanyIds });
      trademoTrackingEvents(trackingEventDictionary.workspaceCompanyRemoveConfirm, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceId, companies_count: workspaceCompanyIds.length });
      const removedIds = new Set(workspaceCompanyIds);
      const deletedCount = data?.deleted_count ?? workspaceCompanyIds.length;
      setWorkspaceCompanies((prev) => prev.filter((row) => row.id === undefined || !removedIds.has(row.id)));
      setWorkspaceCompaniesCount((prev) => Math.max(0, prev - deletedCount));
      setWorkspaceList((prev) => prev.map((workspace) => (workspace.id === activeWorkspaceId ? { ...workspace, companies_count: Math.max(0, (workspace.companies_count || 0) - deletedCount) } : workspace)));
      setActiveWorkspace((prev) => (prev.id === activeWorkspaceId ? { ...prev, companies_count: Math.max(0, (prev.companies_count || 0) - deletedCount) } : prev));
      clearSelectedCompanies();
      toast.success(workspaceCompanyIds.length === 1 ? "Company removed from workspace." : `${workspaceCompanyIds.length} companies removed from workspace.`);
      setShowRemoveCompaniesModal(false);
    } catch (error) {
      captureException(error);
      toast.error(getErrorMessage(error, "Failed to remove companies from workspace."));
    }
  };

  const renderEnrichmentOptions = (): ReactNode => {
    const enrichActionLabel = selectedCount > 0 ? "Enrich every enrichment column for selected companies" : "Enrich every enrichment column for every company on this page";
    const enrichTooltip = isEnriching ? "Wait for the enrichment to complete" : !hasConfiguredEnrichmentColumns ? "No enrichment columns are turned on. Click 'Configure' and enable an enrichment column to start enriching." : enrichActionLabel;

    return (
      <div className="tw-relative">
        <div data-tooltip-id="workspace-tooltip" data-tooltip-content={enrichTooltip}>
          <button
            className="tw-h-9 tw-rounded-lg tw-border tw-border-primary-purple-500 tw-bg-primary-purple-500 tw-px-3 tw-text-sm tw-font-medium tw-text-white tw-flex tw-items-center tw-gap-1.5 hover:tw-bg-primary-purple-600 tw-shadow-button disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            disabled={isEnriching || !hasConfiguredEnrichmentColumns}
            onClick={() => {
              trademoTrackingEvents(trackingEventDictionary.workspaceEnrichButtonClick, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspace?.id });
              setColumnEnrichMenuKey(null);
              requestEnrichmentForRows(selectedCount > 0 ? selectedRows : currentPageRows);
            }}>
            <span className="material-symbols-outlined tw-text-base">auto_awesome</span>
            Enrich
          </button>
        </div>
      </div>
    );
  };

  const renderWorkspaceContent = (): ReactNode => {
    if (isWorkspaceLoading) return <Loader />;
    if (!isWorkspaceLoading && !hasWorkspace) {
      return <WorkspaceEmptyState userName={userName} />;
    }
    return (
      <div className="tw-text-gray-900">
        <div className="tw-px-5">
          <WorkspaceTabs
            workspaceList={workspaceList}
            activeWorkspace={activeWorkspace}
            onWorkspaceSelect={(workspace) => {
              setActiveWorkspace(workspace);
              setPageNumber(1);
              setSelectedRows([]);
              setClearSelection(true);
              trademoTrackingEvents(trackingEventDictionary.workspaceTabSwitch, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_name: workspace?.name, workspace_id: workspace?.id });
            }}
            onDeleteWorkspace={(workspace) => setWorkspaceToDelete(workspace)}
          />
        </div>

        {hasWorkspaceCompanies && (
          <div className="tw-flex tw-justify-between tw-my-4 tw-flex-wrap tw-px-5">
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
              {selectedCount > 0 ? (
                <span className="tw-flex tw-items-center tw-gap-1 tw-text-base">
                  <span className="tw-rounded-md tw-bg-gray-900 tw-px-1.5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-leading-none tw-font-medium tw-text-white">{selectedCount}</span> selected
                  <button type="button" className="tw-ml-1 tw-h-6 tw-w-6 tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50" aria-label="Clear selected companies" onClick={clearSelectedCompanies}>
                    <span className="material-symbols-outlined tw-text-sm">close</span>
                  </button>
                </span>
              ) : (
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-[320px] tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-h-9">
                  <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#667085">
                    <path d="M765-144 526-383q-30 22-65.79 34.5-35.79 12.5-76.18 12.5Q284-336 214-406t-70-170q0-100 70-170t170-70q100 0 170 70t70 170.03q0 40.39-12.5 76.18Q599-464 577-434l239 239-51 51ZM384-408q70 0 119-49t49-119q0-70-49-119t-119-49q-70 0-119 49t-49 119q0 70 49 119t119 49Z" />
                  </svg>
                  <input className="tw-w-full tw-border-none tw-outline-none tw-text-sm tw-text-gray-700" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by company name" />
                </div>
              )}
              <WorkspaceDownloadControl selectedCount={selectedCount} totalRows={availableWorkspaceRows} loading={isDownloading} onDownload={handleWorkspaceDownload} />
              <div data-tooltip-id="workspace-tooltip" data-tooltip-content={selectedCount === 0 ? noSelectionTooltipMessage : selectedCount > 100 ? "Please select upto 100 companies" : ""}>
                {
                  // @ts-ignore — untyped JS component
                  <DynamicDropdown
                    isOpen={showContactsDrop}
                    onToggle={() => {
                      if (selectedCount === 0 || selectedCount > 100) return;
                      if (!showContactsDrop) {
                        trademoTrackingEvents(trackingEventDictionary.workspaceFetchContactsClick, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceIdRef.current, companies_count: selectedCount });
                      }
                      setShowContactsDrop(!showContactsDrop);
                    }}
                    onClose={() => setShowContactsDrop(false)}
                    icon={{
                      before: true,
                      onOpen: "contacts",
                      onClose: "contacts",
                    }}
                    buttonLabel={"Fetch Contacts"}
                    options={contactsReqTypes}
                    buttonId="workspace-fetch-contacts-btn"
                    disabled={selectedCount === 0 || selectedCount > 100}
                    tooltipConfig={{
                      id: "workspace-fetchcontact",
                      message: selectedCount === 0 ? noSelectionTooltipMessage : selectedCount > 100 ? "Please select upto 100 companies" : "Request contacts for selected companies",
                    }}
                    buttonClassName="tw-h-9 tw-font-medium tw-text-sm"
                    dropdownClassName="tw-shadow-sideDrawer tw-border-gray-300 tw-min-w-[220px] tw-left-1"
                  />
                }
              </div>
              {selectedCount > 0 && renderEnrichmentOptions()}
              {renderActionsButton(selectedCount === 0)}
            </div>

            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-relative" ref={filtersRef}>
                <div className={`tw-h-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-text-sm tw-font-medium tw-text-gray-700 tw-flex tw-items-center hover:tw-bg-gray-50 ${showFiltersPanel ? "tw-bg-gray-50" : ""}`}>
                  <button type="button" className="tw-flex tw-items-center tw-gap-1.5 tw-h-full tw-px-3 tw-border-0 tw-bg-transparent tw-text-sm tw-font-medium tw-text-gray-700 tw-cursor-pointer" onClick={() => setShowFiltersPanel((prev) => !prev)}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                      <path d="M456-160v-240h72v80h272v72H528v88h-72Zm-296-88v-72h224v72H160Zm160-160v-88H160v-72h160v-80h72v240h-72Zm160-88v-72h320v72H480Zm160-160v-240h72v80h128v72H712v88h-72Zm-480-88v-72h400v72H160Z" />
                    </svg>
                    Filters
                  </button>
                  {countActiveFilters(workspaceFilters) > 0 && (
                    <button type="button" className="tw-flex tw-items-center tw-gap-0.5 tw-rounded-full tw-bg-primary-purple-500 tw-text-white tw-text-xs tw-font-semibold tw-px-1.5 tw-h-5 tw-mr-2 tw-border-0 tw-cursor-pointer hover:tw-bg-primary-purple-600" onClick={handleClearAllFilters}>
                      {countActiveFilters(workspaceFilters)}
                      <span className="material-symbols-outlined tw-text-[12px] tw-leading-none">close</span>
                    </button>
                  )}
                </div>
                {showFiltersPanel && (
                  <div className="tw-absolute tw-top-[calc(100%+8px)] tw-right-0 tw-z-30">
                    <WorkspaceFiltersPanel appliedFilters={workspaceFilters} onApplyFilter={handleApplyFilter} />
                  </div>
                )}
              </div>
              <button
                className="tw-h-9 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-text-sm tw-font-medium tw-text-gray-700 tw-flex tw-items-center tw-gap-1.5 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                disabled={isEnriching}
                onClick={() => {
                  setColumnConfigMode("all");
                  setShowColumnConfigModal(true);
                  trademoTrackingEvents(trackingEventDictionary.workspaceColumnConfigureOpen, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspace?.id, mode: "all" });
                }}>
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                  <path d="M192-744v528h228v-528H192Zm-72 600v-672h672v144H719.51v-72H492v528h228v-72h72v144H120Zm372-336Zm-72 0h72-72Zm0 0Zm300 120v-84h-84v-72h84v-84h72v84h84v72h-84v84h-72Z" />
                </svg>
                Configure
              </button>
              {selectedCount === 0 && renderEnrichmentOptions()}
            </div>
          </div>
        )}

        <div ref={tableWrapperRef} className="tw-bg-white tw-overflow-hidden">
          {isTableLoading ? (
            <div className="tw-p-3">
              {
                // @ts-ignore — untyped JS component
                <LoadingSkeleton lines={10} lineHeight="44px" width="100%" shape="rectangle" />
              }
            </div>
          ) : (
            // @ts-ignore — untyped JS component
            <ReactTableTrademo
              columns={tableColumns}
              data={workspaceCompanies}
              paginationTotalRows={workspaceCompaniesCount}
              onSelectedRowsChange={(rows) => setSelectedRows(rows || [])}
              paginationPerPage={rowPerPage}
              paginationDefaultPage={pageNumber}
              selectedData={selectedRows}
              onChangeRowsPerPage={handleTrademoTableSearchPerRowsChange}
              onTableObjectChange={updateTrademoTableObject}
              onChangePage={handleTrademoTablePageChange}
              clearSelectedRows={clearSelection}
              hideSorting={true}
              hideBorder={false}
              options={[25, 50]}
              noDataComponent={
                hasSearchQuery || hasActiveFilters ? (
                  <span className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    {hasSearchQuery ? `No companies found for "${debouncedSearchTerm}"` : "No companies match the applied filters"}
                    <button
                      type="button"
                      className="tw-bg-transparent tw-border-0 tw-p-0 tw-text-primary-purple-500 tw-text-sm tw-font-medium tw-underline tw-cursor-pointer hover:tw-text-primary-purple-600"
                      onClick={() => {
                        setSearchTerm("");
                        handleClearAllFilters();
                      }}>
                      {hasSearchQuery && hasActiveFilters ? "Clear Search / Filters" : hasSearchQuery ? "Clear Search" : "Clear Filters"}
                    </button>
                  </span>
                ) : (
                  "No companies added to this workspace"
                )
              }
              StyledComponent={CustomStylesWorkspaceTable}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <IntelLayout type="workspace" pageTitle="Workspace | Trademo Intel" sidebarRef={sidebarSetterRef}>
      <Tooltip id="workspace-tooltip" className="tw-z-50 tw-max-w-[300px] !tw-opacity-100" />
      <Tooltip id="workspace-tabs-tooltip" place="bottom" opacity={1} className="tw-z-50 tw-max-w-[360px] !tw-bg-white !tw-text-gray-900 !tw-border-gray-200 !tw-rounded-lg !tw-shadow-lg !tw-text-sm" />
      {renderWorkspaceContent()}
      {showCriteriaModal && <WorkspaceListCriteriaModal show={showCriteriaModal} onHide={() => setShowCriteriaModal(false)} workspaceName={activeWorkspace?.name} searchPayload={(activeWorkspace?.search_payload ?? activeWorkspace?.filters_json) as Record<string, unknown> | null} />}
      <ConfirmActionModal
        show={!!workspaceToDelete}
        message={
          <>
            Are you sure to delete <span className="tw-font-semibold">{workspaceToDelete?.name || "this list"}</span> from workspace? This will also delete all the data points related to the companies present in this list.
          </>
        }
        onCancel={() => setWorkspaceToDelete(null)}
        onConfirm={handleConfirmDeleteWorkspace}
      />
      <ConfirmActionModal
        show={showRemoveCompaniesModal}
        message={
          <>
            Are you sure to delete{" "}
            <span className="tw-font-semibold">
              {selectedCount} {selectedCount === 1 ? "company" : "companies"}
            </span>{" "}
            from <span className="tw-font-semibold">{activeWorkspace?.name || "this list"}</span> in workspace? This will also delete all the data points related to {selectedCount === 1 ? "this company" : "these companies"}.
          </>
        }
        onCancel={() => setShowRemoveCompaniesModal(false)}
        onConfirm={handleConfirmRemoveSelectedCompanies}
      />
      {showColumnConfigModal && <ColumnConfigurationModal show={showColumnConfigModal} onHide={() => setShowColumnConfigModal(false)} columns={workspaceColumns} onSave={handleSaveColumnConfiguration} mode={columnConfigMode} />}
      <EnrichmentInputsModal
        show={showEnrichmentInputsModal}
        onHide={() => {
          setShowEnrichmentInputsModal(false);
          setPendingEnrichmentRows(null);
        }}
        onSubmit={handleEnrichmentInputsSubmit}
        hasPricing={hasPricingAgentInView}
        hasContact={hasEnrichedContactInView}
        hasCompanyInfo={hasCompanyEnrichmentInView}
        hasSanctions={hasSanctionsInView}
        hasTraderManufacturer={hasTraderManufacturerInView}
        searchKey={activeWorkspace?.filters_json?.search as string | undefined}
        remainingCredits={remainingValueAddCount}
      />
      {showAiSummaryModal && (
        <Modal centered size="xl" show={showAiSummaryModal} onHide={() => setShowAiSummaryModal(false)} animation={false}>
          <Modal.Header closeButton>
            <Modal.Title className="tw-capitalize">AI Summary{aiSummaryCompanyName ? ` - ${aiSummaryCompanyName}` : ""}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className={`tw-border tw-border-gray-200 tw-rounded-lg tw-flex-grow ${activeAiSummaryHtml ? "tw-py-6 tw-px-6" : "tw-w-full tw-h-[420px] tw-flex tw-items-center tw-justify-center"}`}>
              {activeAiSummaryHtml ? (
                <div className="tw-text-sm tw-font-normal" dangerouslySetInnerHTML={{ __html: formatAiSummaryHtml(activeAiSummaryHtml) }} />
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-10 tw-items-center">
                  <img src={`${CLOUDINARY_INTEL_BASE_URL}/v1767707070/intel/misc/empty_state__for_hermes_ai_wjyaat.svg`} alt="no-summary-found" />
                  <span className="tw-text-base tw-font-semibold">No significant insights found</span>
                </div>
              )}
            </div>
            {activeAiSummaryHtml ? (
              <div className="tw-text-[#667085] tw-text-sm tw-font-medium tw-p-2 tw-pt-4 tw-flex tw-items-center">
                <span className="material-symbols-outlined tw-mr-1">info</span>
                <span>AI Generated summary may not be 100% accurate all times. Use Human review for important decisions.</span>
              </div>
            ) : null}
          </Modal.Body>
        </Modal>
      )}
      <PricingAgentPane open={showPricingPane} onClose={() => setShowPricingPane(false)} companyName={pricingCompanyName} pricing={activePricing} />
      <SanctionsScreeningPane open={showSanctionsPane} onClose={() => setShowSanctionsPane(false)} companyName={sanctionsCompanyName} matches={activeSanctions} />
      {showFavModal && (
        // @ts-ignore — untyped JS component
        <AddFavModal profileType="company" show={showFavModal} handleClose={() => setShowFavModal(false)} prospectData={selectedRows} onAddSuccess={() => trademoTrackingEvents(trackingEventDictionary.workspaceActionsBookmarkConfirm, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspace?.id, companies_count: selectedRows.length })} />
      )}
      {showContactModal ? (
        // @ts-ignore — untyped JS component
        <ContactModal show={showContactModal} prospectData={selectedRows} currentUserPlan={currentUserPlan} handleClose={() => setShowContactModal(false)} type={"importer"} closeEventHandler={() => null} remainingValueAddCount={remainingValueAddCount} onConfirm={() => trademoTrackingEvents(trackingEventDictionary.workspaceFetchContactsConfirm, { ...generateTrackingObject("workspace", window.location.pathname, getUserActivePlan()), workspace_id: activeWorkspaceIdRef.current, companies_count: selectedRows.length })} />
      ) : null}
      {sourcesModalCompanyId !== null && (
        <Modal centered size="xl" show={sourcesModalCompanyId !== null} onHide={() => setSourcesModalCompanyId(null)} animation={false}>
          <Modal.Header closeButton>
            <Modal.Title className="tw-text-base tw-font-semibold">Enrichment Sources</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-p-4 tw-min-h-[200px] tw-max-h-[60vh] tw-overflow-auto">
              {sourcesLoading && <div className="tw-flex tw-items-center tw-justify-center tw-h-40 tw-text-sm tw-text-gray-500">Loading...</div>}
              {sourcesError && <div className="tw-text-sm tw-text-red-500">{sourcesError}</div>}
              {!sourcesLoading && !sourcesError && sourcesData !== null && <pre className="tw-text-xs tw-text-gray-800 tw-whitespace-pre-wrap tw-break-all tw-m-0 tw-font-mono">{JSON.stringify(sourcesData, null, 2)}</pre>}
            </div>
          </Modal.Body>
        </Modal>
      )}
    </IntelLayout>
  );
};

export default Workspace;
